import Utils from '@/common/utils/utils';
import { IconButton, Tooltip, Menu, MenuItem } from '@material-ui/core';
import { useSetRecoilState } from 'recoil';
import { closePanelTabSelector, setPanelSelector } from '@/state/selector/panelSelector';

import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';

import { useRecoilValue } from 'recoil';
import { userAtom, userPermissionToViewKnowledgeAtom } from '@/state/atom/userAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { isShowVisibilityKnowledgeAtom } from '@/state/atom/ticketAtom';

import { getUserListCanMention } from '../handler/ChatThreadHandler';
import { getChatPostListByChatThreadId } from '@/service/chatPostService';
import { getAllChatThreadId } from '../handler/MessageHandler';
import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { generateTicketOutlineOnTabChat, handleViewTicketDetailByChat } from '@/component/ticketList/handler/TicketHandler';
import { multiPanelAtom } from '@/state/atom/panelAtom';
import { isEmailTicket } from '../handler/ChatPostHandler';
import { updateChatThreadInfo } from '@/service/chatThreadService';
import PropTypes from 'prop-types';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import CorporationUtils from '@/common/utils/CorporationUtils';
import ChatPostOnDemandControl from '@/component/multiLanguage/elements/ChatPostOnDemandControl';
import useOnDemandOutlineDisplay from '@/component/multiLanguage/hooks/useOnDemandOutlineDisplay';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';

const getAIAssistantToggleValueFromStatus = (aiAssistantStatus) => {
    return aiAssistantStatus === TelephoneConstant.AI_ASSISTANT_STATUS.ON;
};

const getIsAIAssistantEnabled = (threadId, aiAssistantStatus, overrideByThreadIdMap) => {
    if (threadId && overrideByThreadIdMap.has(threadId)) {
        return overrideByThreadIdMap.get(threadId);
    }

    return getAIAssistantToggleValueFromStatus(aiAssistantStatus);
};

export const TicketHead = ({ ...props }) => {
    // Re-render when the translation dictionary loads/extends so tr() text updates.
    useTranslationSubscription();

    const panelList = useRecoilValue(multiPanelAtom);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);

    const addNewPanel = useSetRecoilState(setPanelSelector);
    // common userDetail state
    const userDetail = useRecoilValue(userAtom);
    const userPermissionView = useRecoilValue(userPermissionToViewKnowledgeAtom);
    const setIsShowVisibilityKnowledgeAtom = useSetRecoilState(isShowVisibilityKnowledgeAtom);

    const selectedProject = useRecoilValue(projectInuseDetailAtom);
    const isArchive = props.ticketInfo.liveOrArchived === Constant.LIVE_OR_ARCHIVED.ARCHIVED;
    const aiAssistantSourceTicket =
        props?.ticketOriginInfor?.ticketType === Constant.TICKET_TYPE.TELEPHONE
            ? props.ticketOriginInfor
            : props?.ticketInfo;
    const localAiAssistantToggleOverrideByThreadIdRef = useRef(new Map());
    const aiAssistantToggleOverrideByThreadIdRef =
        props.aiAssistantToggleOverrideByThreadIdRef || localAiAssistantToggleOverrideByThreadIdRef;
    const aiAssistantThreadId = props?.aiAssistantThread?.id;
    const aiAssistantThreadStatus = props?.aiAssistantThread?.aiAssistantStatus;

    const [isAIAssistantEnabled, setIsAIAssistantEnabled] = useState(() =>
        getIsAIAssistantEnabled(
            aiAssistantThreadId,
            aiAssistantThreadStatus,
            aiAssistantToggleOverrideByThreadIdRef.current
        )
    );
    const [isUpdatingAIAssistantToggle, setIsUpdatingAIAssistantToggle] = useState(false);

    // Header options dropdown (⋮): holds 重ね表示 / AI Assistant / 翻訳版 toggles.
    const [anchorHeadOptions, setAnchorHeadOptions] = useState(null);
    const isOpenHeadOptions = Boolean(anchorHeadOptions);

    // Show the 翻訳版 toggle only when the org has multilingual ON and the thread
    // is not the AI Assistant thread (AI already replies in the user's language).
    const isShowTranslationToggle =
        CorporationUtils.isEnableSettingMultiLanguage(props.corporationDetail) &&
        props?.selectThread?.threadType !== Constant.THREAD_TYPE.AI_ASSISTANT;

    const isAiAssistantSettingOff = () => {
        return (
            !Utils.isCorporationOnAIAuto(props.corporationDetail) ||
            !Utils.isEnableSettingTelephoneAgent(props.corporationDetail) ||
            !Utils.isProjectDeskOn(selectedProject)
        );
    };

    useEffect(() => {
        props?.setIsChatThreadOnAiAssistant(isAIAssistantEnabled)
    },[isAIAssistantEnabled])

    useEffect(() => {
        return () => {
            if (!props.aiAssistantToggleOverrideByThreadIdRef) {
                localAiAssistantToggleOverrideByThreadIdRef.current.clear();
            }
        };
    }, []);

    useEffect(() => {
        const currentThreadId = aiAssistantThreadId;
        if (Utils.isEmptyString(currentThreadId)) {
            return;
        }

        const toggleOverrideMap = aiAssistantToggleOverrideByThreadIdRef.current;

        toggleOverrideMap.forEach((_, threadId) => {
            if (threadId !== currentThreadId) {
                toggleOverrideMap.delete(threadId);
            }
        });
    }, [aiAssistantThreadId]);

    // Force OFF AI Assistant toggle when corp/project setting changes realtime
    useEffect(() => {
        if (isAiAssistantSettingOff()) {
            setIsAIAssistantEnabled(false);
            const threadId = aiAssistantThreadId;
            // Only call API if thread exists and status is currently ON
            if (threadId && aiAssistantThreadStatus === TelephoneConstant.AI_ASSISTANT_STATUS.ON) {
                if (props?.setAiAssistantThread) {
                    props.setAiAssistantThread(prev => ({
                        ...prev,
                        aiAssistantStatus: TelephoneConstant.AI_ASSISTANT_STATUS.OFF
                    }));
                }
                // Persist OFF to DB
                updateChatThreadInfo({
                    id: threadId,
                    aiAssistantStatus: TelephoneConstant.AI_ASSISTANT_STATUS.OFF,
                    aiAssistantStatusUpdatedAt: new Date().toISOString()
                }).catch(() => {});
            }

            // Update override ref to OFF
            aiAssistantToggleOverrideByThreadIdRef.current.set(threadId, false);
        }
    }, [props.corporationDetail, selectedProject]);

    useEffect(() => {
        const threadId = aiAssistantThreadId;
        const valueFromSource = getAIAssistantToggleValueFromStatus(aiAssistantThreadStatus);
        const toggleOverrideMap = aiAssistantToggleOverrideByThreadIdRef.current;

        // Force OFF when AI Auto or Voice Agent setting is OFF
        if (isAiAssistantSettingOff()) {
            setIsAIAssistantEnabled(false);
            return;
        }

        if (threadId) {
            const overrideValue = toggleOverrideMap.get(threadId);
            const hasOverride = typeof overrideValue === 'boolean';

            if (hasOverride && overrideValue === valueFromSource) {
                toggleOverrideMap.delete(threadId);
                setIsAIAssistantEnabled(valueFromSource);
                return;
            }

            if (hasOverride) {
                setIsAIAssistantEnabled(overrideValue);
                return;
            }

            setIsAIAssistantEnabled(valueFromSource);
            return;
        }

        setIsAIAssistantEnabled(valueFromSource);
    }, [
        aiAssistantThreadId,
        aiAssistantThreadStatus
    ]);

    const isTelephoneAIAssistantFeatureVisible = () => {
        if (Utils.isB2CUser(userDetail)) {
            return false;
        }

        const hasAIAssistantThread =
            !Utils.isEmptyString(aiAssistantSourceTicket?.aiAssistantChatThread) &&
            !Utils.isEmptyObject(props?.aiAssistantThread);

        return (
            aiAssistantSourceTicket?.ticketType === Constant.TICKET_TYPE.TELEPHONE &&
            aiAssistantSourceTicket?.isEnableTelephoneAgent !== false &&
            hasAIAssistantThread
        );
    };

    const isDisableAIAssistant = () => {
        const isAIAssistantTicketArchive =
            aiAssistantSourceTicket?.liveOrArchived === Constant.LIVE_OR_ARCHIVED.ARCHIVED;
        const isAIAssistantTicketDeleted = !Utils.isEmptyString(aiAssistantSourceTicket?.deletedAt);

        return (
            isAIAssistantTicketArchive ||
            isAIAssistantTicketDeleted ||
            Utils.isEmptyString(aiAssistantThreadId) ||
            isUpdatingAIAssistantToggle ||
            isAiAssistantSettingOff()
        );
    };

    const onChangeAIAssistantToggle = async () => {
        if (
            isUpdatingAIAssistantToggle ||
            Utils.isEmptyString(aiAssistantThreadId)
        ) {
            return;
        }
        props.setIsLoading(true);
        const nextValue = !isAIAssistantEnabled;
        const previousValue = isAIAssistantEnabled;
        const threadId = aiAssistantThreadId;

        // Optimistic update
        if (threadId) {
            aiAssistantToggleOverrideByThreadIdRef.current.set(threadId, nextValue);
        }
        setIsAIAssistantEnabled(nextValue);
        setIsUpdatingAIAssistantToggle(true);
        
        try {
            if (Utils.isEmptyString(aiAssistantThreadId)) {
                throw new Error('Chat Thread id is empty');
            }

            const aiAssistantStatus = nextValue
                ? TelephoneConstant.AI_ASSISTANT_STATUS.ON
                : TelephoneConstant.AI_ASSISTANT_STATUS.OFF;
            const aiAssistantStatusUpdatedAt = new Date().toISOString();

            const result = await updateChatThreadInfo({
                id: aiAssistantThreadId,
                aiAssistantStatus,
                aiAssistantStatusUpdatedAt
            });

            if (!result || Object.keys(result).length === 0) {
                throw new Error('updateChatThreadInfo returned empty result');
            }
        } catch (error) {
            console.error('Failed to update AI Assistant toggle:', error);
            if (threadId) {
                aiAssistantToggleOverrideByThreadIdRef.current.set(threadId, previousValue);
            }
            setIsAIAssistantEnabled(previousValue);
        } finally {
            props.setIsLoading(false);
            setIsUpdatingAIAssistantToggle(false);
        }
    };

    const onOpenKnowledge = () => {
        const existedKnowledgeHelpdeskTicketListPanel = panelList.find(
            panel => (Constant.PANEL_TYPE.KNOWLEDGE_HELPDESK_TICKET_LIST, Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST).includes(panel.type)
        );
        
        if (!Utils.isEmptyObject(existedKnowledgeHelpdeskTicketListPanel)) {
            closePanelTab({
                event: null,
                panelType: existedKnowledgeHelpdeskTicketListPanel.type,
                panelId: existedKnowledgeHelpdeskTicketListPanel.id,
                tabId: 'knowledge_ticket_list_tab'
            });
        }

        let detailTicket = props.ticketInfo;
        
        let propsClone = {
            API: props.API,
            tabId: props.tabId,
            panelId: props?.panelId,
            chatThreadId: props?.chatThreadId,
            panelType: props?.panelType,
            componentName: props?.componentName,
        }
        let addNewPanelParams = {
            type: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST,
            componentName: Constant.COMPONENT_NAME.LIST_TICKET_KNOWLEDGE,
            tabId: 'knowledge_ticket_list_tab',
            visibilitySettingType: Constant.VISIBILITY_SETTING_TYPE.PRIVATE,
            isCustomer: false,
            customerId: '',
            projectSelectedId: userPermissionView.projectSelectedId,
            corporationSelectedId: userPermissionView.corporationSelectedId,
            props : {
                ...propsClone,
                componentName: Constant.COMPONENT_NAME.LIST_TICKET_KNOWLEDGE,
                panelType: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST
            }
        }
        if (
            userPermissionView.projectSelectedId !== detailTicket.affiliationProject &&
            (userDetail?.id === detailTicket.customer || userPermissionView.projectSelectedId === detailTicket.customer)
        ) {
            setIsShowVisibilityKnowledgeAtom(false);

            addNewPanelParams.visibilitySettingType = Constant.VISIBILITY_SETTING_TYPE.CUSTOMER
            addNewPanelParams.isCustomer = true
            addNewPanelParams.customerId = detailTicket.customer
            addNewPanelParams.projectSelectedId = detailTicket.affiliationProject
            addNewPanelParams.corporationSelectedId = detailTicket.affiliationOrganization
        }
        addNewPanel(addNewPanelParams)                                                                          
    };

    const onChangeShowAllChatPost = async () => {
        props.setIsLoading(true);
        
        let isShowAllChatPost = !props.isShowAllChatPost;
        await updateShowAllPostToWorkspace(isShowAllChatPost);

        let threadIdList = getAllChatThreadId(props, isShowAllChatPost, props.selectThread);
        let fromStart = 0;
        props.setFromMsg(fromStart);

        let postList = await getChatPostListByChatThreadId(props?.API, threadIdList, fromStart, selectedProject.id);
        let userListMention = await getUserListCanMention(props);
        userListMention = userListMention?.filter(user => user.active === 1)
        props.setUserListCanMention(userListMention);

        if (!Utils.isEmptyObject(postList)) {
            setTimeout(() => {
                props.setUserChatList(postList.userChatList);
            });
            props.setChatPostAllList({
                type: Constant.ACTION_CHANGE_LIST_DATA_TYPE.INITIAL,
                posts: postList ? postList.chatPostAllList : [],
                isReload: true
            });
            props.setLastedCountList(postList.chatPostAllList.length);
        }

        props.setIsShowAllChatPost(isShowAllChatPost);
        props.setIsLoading(false);
    };

    const updateShowAllPostToWorkspace = async (isShowAll) => {
        let panelId = props.panelId;
        let tabId = props.tabId;
        let panelListClone = cloneDeep(props.panelList);

        panelListClone.forEach(panel => {
            if(panel.id === panelId) {
                panel.tabList.forEach(tab => {
                    if(tab.id === tabId) {
                        tab.component.props.isShowAllChatPost = isShowAll;
                    }
                })
            }
        })
        props.setPanelList(panelListClone);
    }
    const titleOfTicketInfo = useMemo(() => {
        return generateTicketOutlineOnTabChat(props?.ticketInfo)
    }, [props?.ticketInfo]);

    // On-demand translation: show the translated title while this thread's
    // 「翻訳版」toggle is ON, otherwise the original outline.
    const { displayOutline: titleToDisplay } = useOnDemandOutlineDisplay(
        titleOfTicketInfo,
        props?.chatThreadId,
    );

    const isShowBtnKnowledge = () => {
        if(isEmailTicket(props.ticketDetailSelected)) {
            return true;
        }
        if (
            props.ticketDetailSelected?.ticketType !== Constant.TICKET_TYPE.KNOWLEDGE ||
            props.ticketDetailSelected?.ticketType !== Constant.TICKET_TYPE.MASTER_KNOWLEDGE
        ) {
            return false;
        }
        
        if (userPermissionView.type !== Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_PARTNER) {
            if(props.ticketDetailSelected.knowledgeService === Constant.STATUS_SERVICE_PERMISSION.OFF) {
                return false;
            }
            
            if (
                (props.corporationDetail?.knowledgeService === Constant.STATUS_SERVICE_PERMISSION.OFF &&
                    props.selectedProjectId === props.ticketInfo?.affiliationProject) ||
                [Constant.TICKET_TYPE.BROADCAST_PARENT, Constant.TICKET_TYPE.BROADCAST_CHILD].includes(
                    props.ticketDetailSelected?.ticketType
                )
            ) {
                return false;
            }
            return true;

        } else {
            if (userPermissionView.knowledgeService !== Constant.STATUS_SERVICE_PERMISSION.ON) {
                return false;
            }
            return true;
        }
    };

    const TooltipInKnowledge = () => {
        let toolTipTxt = '';
        if (
            [
                Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD,
                Constant.PANEL_TYPE.HELPDESK_CHATTHREAD
            ].includes(props?.panelType)
        ) {
            toolTipTxt = trI18n('KNOWLITT001');
        }
        return toolTipTxt;
    };
    const isAIAssistantToggleVisible = isTelephoneAIAssistantFeatureVisible();
    const isShowStackedThreadToggle = props.threadChatList?.length > 1;

    // The ⋮ dropdown is shown only when at least one of its items is visible.
    const isShowHeadOptionsMenu =
        (props.isShowHeadlineButtonGroup && isShowStackedThreadToggle) ||
        isAIAssistantToggleVisible ||
        isShowTranslationToggle;

    return (
        <div className="headTicket">
            <div
                className={`headTicket-chat ${
                    isAIAssistantToggleVisible ? 'headTicket-chat--with-ai-assistant' : ''
                }`}
            >
                <div className="headTicket-fl">
                    <div
                        className={`headline ${props.isDisplayTicketHeadLine ? '' : 'cursor-df'}`}
                        onClick={() => {
                            if (props.isDisplayTicketHeadLine) {
                                handleViewTicketDetailByChat(
                                    props,
                                    props.selectThread.ticketId || props.ticketId,
                                    props.ticketInfo,
                                    addNewPanel,
                                )
                            }
                        }}
                    >
                        <div className="headline__thumb">
                            <img src="/assets/image/icon-check.svg" alt="" />
                        </div>
                        {props.isDisplayTicketHeadLine && (
                            <div className="headline__content">
                                <OverflowTooltipContent
                                    text={titleToDisplay}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="headTicket-rg">
                    {/* Knowledge link stays outside the dropdown. */}
                    {props.isShowHeadlineButtonGroup && isShowBtnKnowledge() && (
                        <div className="itemButton">
                            <Tooltip title={TooltipInKnowledge()} placement="top" arrow>
                                <IconButton
                                    className="itemNav"
                                    onClick={onOpenKnowledge}
                                    disabled={
                                        isArchive ||
                                        !Utils.isEmptyString(props.ticketInfo.deletedAt)
                                    }
                                >
                                    <img src="/assets/image/icon-link.svg" alt="" />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}

                    {/* Header options dropdown (⋮): 重ね表示 / AI Assistant / 翻訳版. */}
                    {isShowHeadOptionsMenu && (
                        <div className="headOptions">
                            <IconButton
                                className="itemNav"
                                aria-label="more"
                                aria-haspopup="true"
                                aria-expanded={isOpenHeadOptions ? 'true' : undefined}
                                onClick={(e) => setAnchorHeadOptions(e.currentTarget)}
                                data-cy="chat-head-options-button"
                            >
                                <img src="/assets/image/icon-more-panel.svg" alt="" />
                            </IconButton>

                            <Menu
                                anchorEl={anchorHeadOptions}
                                open={isOpenHeadOptions}
                                onClose={() => setAnchorHeadOptions(null)}
                                getContentAnchorEl={null}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                {props.isShowHeadlineButtonGroup && isShowStackedThreadToggle && (
                                    <MenuItem disableRipple className="headOptions-item">
                                        <div className="ticketSwitch">
                                            <p className="ticketSwitch-label">{tr('重ね表示')}</p>
                                            <div className="switch-check">
                                                <input
                                                    type="checkbox"
                                                    id={`switch-show-all-chat-post-${props.panelId}`}
                                                    defaultChecked={props.isShowAllChatPost}
                                                    onChange={onChangeShowAllChatPost}
                                                    disabled={
                                                        !Utils.isEmptyString(props.ticketInfo.deletedAt)
                                                    }
                                                />
                                                <label
                                                    htmlFor={`switch-show-all-chat-post-${props.panelId}`}
                                                ></label>
                                            </div>
                                        </div>
                                    </MenuItem>
                                )}
                                {isAIAssistantToggleVisible && (
                                    <MenuItem disableRipple className="headOptions-item">
                                        <div className="ticketSwitch ticketSwitch--ai-assistant">
                                            <p className="ticketSwitch-label">{trI18n('AI_ASSISTANT')}</p>
                                            <div className="switch-check">
                                                <input
                                                    type="checkbox"
                                                    id={`switch-ai-assistant-${props.panelId}`}
                                                    checked={isAIAssistantEnabled}
                                                    onChange={onChangeAIAssistantToggle}
                                                    disabled={isDisableAIAssistant()}
                                                />
                                                <label htmlFor={`switch-ai-assistant-${props.panelId}`}></label>
                                            </div>
                                        </div>
                                    </MenuItem>
                                )}
                                {isShowTranslationToggle && (
                                    <MenuItem disableRipple className="headOptions-item">
                                        <ChatPostOnDemandControl
                                            threadId={props?.chatThreadId}
                                            ticketInfo={props?.ticketInfo}
                                            isOverlap={props?.isShowAllChatPost}
                                        />
                                    </MenuItem>
                                )}
                            </Menu>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

TicketHead.propTypes = {
    API: PropTypes.any,
    isDisplayTicketHeadLine: PropTypes.any,
    handleClickViewTicketDetail: PropTypes.any,
    handleViewTicketDetailByChat: PropTypes.any,
    selectThread: PropTypes.any,
    ticketId: PropTypes.any,
    ticketInfo: PropTypes.any,
    setIsLoading: PropTypes.any,
};
